/**
 * Frontend Performance Profiler
 * Usage:
 *   profiler.start('operation-name')
 *   // ... do work ...
 *   profiler.end('operation-name')
 *
 * Or for async operations:
 *   const timer = profiler.startTimer('operation-name')
 *   // ... do async work ...
 *   timer.end()
 *
 * At the end of a flow:
 *   profiler.summary('Flow Name')
 */

interface TimingEntry {
  name: string
  startTime: number
  endTime?: number
  duration?: number
  parent?: string
}

class FrontendProfiler {
  private timings: Map<string, TimingEntry> = new Map()
  private activeTimers: Map<string, number> = new Map()
  private flowStartTime: number = 0
  private enabled: boolean = true // Disable via window.profiler.disable() in console
  private callCounts: Map<string, number> = new Map()

  enable() {
    this.enabled = true
  }

  disable() {
    this.enabled = false
  }

  reset() {
    this.timings.clear()
    this.activeTimers.clear()
    this.callCounts.clear()
    this.flowStartTime = 0
  }

  startFlow(name: string) {
    if (!this.enabled) return
    this.reset()
    this.flowStartTime = performance.now()
    console.log(`\n[PROF] ══════════════════════════════════════════`)
    console.log(`[PROF] PROFILER: Starting flow "${name}"`)
    console.log(`[PROF] ══════════════════════════════════════════\n`)
  }

  start(name: string, parent?: string) {
    if (!this.enabled) return

    const now = performance.now()
    this.activeTimers.set(name, now)

    // Track call counts
    const count = (this.callCounts.get(name) || 0) + 1
    this.callCounts.set(name, count)

    const elapsed = this.flowStartTime ? (now - this.flowStartTime).toFixed(1) : '0'
    console.log(`[PROF] [+${elapsed}ms] START: ${name}${count > 1 ? ` (call #${count})` : ''}`)
  }

  end(name: string) {
    if (!this.enabled) return

    const now = performance.now()
    const startTime = this.activeTimers.get(name)

    if (startTime === undefined) {
      console.warn(`[PROF] WARNING: No start time found for "${name}"`)
      return
    }

    const duration = now - startTime
    const elapsed = this.flowStartTime ? (now - this.flowStartTime).toFixed(1) : '0'
    const count = this.callCounts.get(name) || 1

    // Store timing
    const key = count > 1 ? `${name} #${count}` : name
    this.timings.set(key, {
      name: key,
      startTime,
      endTime: now,
      duration,
    })

    this.activeTimers.delete(name)

    const durationStr = duration.toFixed(1)
    const icon = duration > 1000 ? '🔴' : duration > 100 ? '🟡' : '🟢'
    console.log(`[PROF] [+${elapsed}ms] END: ${name} ${icon} ${durationStr}ms`)
  }

  startTimer(name: string, parent?: string) {
    this.start(name, parent)
    return {
      end: () => this.end(name),
    }
  }

  // For measuring synchronous operations inline
  measure<T>(name: string, fn: () => T): T {
    this.start(name)
    try {
      return fn()
    } finally {
      this.end(name)
    }
  }

  // For measuring async operations
  async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
    this.start(name)
    try {
      return await fn()
    } finally {
      this.end(name)
    }
  }

  summary(flowName?: string) {
    if (!this.enabled) return

    const totalTime = performance.now() - this.flowStartTime

    console.log(`\n[PROF] ══════════════════════════════════════════`)
    console.log(`[PROF] PROFILER SUMMARY${flowName ? `: ${flowName}` : ''}`)
    console.log(`[PROF] ══════════════════════════════════════════`)
    console.log(`[PROF] Total flow time: ${totalTime.toFixed(1)}ms`)
    console.log(`[PROF] ──────────────────────────────────────────`)

    // Sort by duration descending
    const sorted = Array.from(this.timings.values())
      .filter((t) => t.duration !== undefined)
      .sort((a, b) => (b.duration || 0) - (a.duration || 0))

    // Show top operations
    console.log(`[PROF] Top operations by duration:`)
    sorted.slice(0, 15).forEach((t, i) => {
      const pct = ((t.duration! / totalTime) * 100).toFixed(1)
      const icon = t.duration! > 1000 ? '🔴' : t.duration! > 100 ? '🟡' : '🟢'
      console.log(`[PROF]   ${i + 1}. ${icon} ${t.name}: ${t.duration!.toFixed(1)}ms (${pct}%)`)
    })

    // Show call counts for repeated operations
    const repeatedOps = Array.from(this.callCounts.entries())
      .filter(([_, count]) => count > 1)
      .sort((a, b) => b[1] - a[1])

    if (repeatedOps.length > 0) {
      console.log(`[PROF] ──────────────────────────────────────────`)
      console.log(`[PROF] Repeated operations:`)
      repeatedOps.forEach(([name, count]) => {
        // Sum up all durations for this operation
        let totalDuration = 0
        for (let i = 1; i <= count; i++) {
          const key = i === 1 ? name : `${name} #${i}`
          const timing = this.timings.get(key)
          if (timing?.duration) totalDuration += timing.duration
        }
        console.log(`[PROF]   ⚠️ ${name}: called ${count}x, total ${totalDuration.toFixed(1)}ms`)
      })
    }

    console.log(`[PROF] ══════════════════════════════════════════\n`)
  }

  // Get raw timing data for analysis
  getTimings() {
    return Array.from(this.timings.values())
  }
}

export const profiler = new FrontendProfiler()

// Make it available globally for debugging in console
if (typeof window !== 'undefined') {
  ;(window as any).profiler = profiler
}
